<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useTranslationPreferences } from 'dashboard/composables/useTranslationPreferences';
import {
  isTranslationHiddenForMessage,
  toggleTranslationHiddenForMessage,
} from '../../translationVisibilityStore';
import { attachmentHasTranslatedImage } from '../../mediaTranslation';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  conversationId,
  id,
  showOriginalTranslationContent,
  toggleTranslatedContentView,
} = useMessageContext();
const route = useRoute();
const { preferences, ensureLoaded } = useTranslationPreferences();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const { t } = useI18n();

const showAutoTranslate = ref(false);
const showTranslationMenu = ref(false);
const isTranslationHidden = ref(false);
const translationEnabled = computed(
  () => preferences.value.translation_enabled ?? true
);

const autoTranslate = computed(() => {
  const attributes = contentAttributes.value || {};
  return attributes.autoTranslate || attributes.auto_translate || {};
});

const autoTranslateOriginal = computed(() => {
  return (
    autoTranslate.value?.originalText || autoTranslate.value?.original_text
  );
});

const hasAutoTranslate = computed(() => !!autoTranslateOriginal.value);

const toggleAutoTranslate = () => {
  showAutoTranslate.value = !showAutoTranslate.value;
};

const isIncomingMessage = computed(
  () => messageType.value === MESSAGE_TYPES.INCOMING
);
const shouldRenderInlineTranslation = computed(
  () =>
    translationEnabled.value &&
    isIncomingMessage.value &&
    hasTranslations.value &&
    !attachments.value?.some(attachmentHasTranslatedImage)
);

const hasTranslatedImages = computed(() =>
  attachments.value?.some(attachmentHasTranslatedImage)
);

const showTranslationToggle = computed(
  () => translationEnabled.value && (hasTranslations.value || hasTranslatedImages.value)
);

const toggleTranslationVisibility = () => {
  toggleTranslationHiddenForMessage(conversationId.value, id.value);
  isTranslationHidden.value = isTranslationHiddenForMessage(
    conversationId.value,
    id.value
  );
};

const translationMenuItems = computed(() => [
  {
    action: 'toggleTranslationVisibility',
    value: 'toggle',
    label: isTranslationHidden.value
      ? t('CONVERSATION.TRANSLATION.SHOW_TRANSLATION')
      : t('CONVERSATION.TRANSLATION.HIDE_TRANSLATION'),
    icon: isTranslationHidden.value ? 'i-lucide-eye' : 'i-lucide-eye-off',
  },
]);

const handleTranslationMenuAction = ({ action }) => {
  if (action === 'toggleTranslationVisibility') {
    toggleTranslationVisibility();
  }
  showTranslationMenu.value = false;
};

const renderContent = computed(() => {
  if (showOriginalTranslationContent.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

onMounted(async () => {
  isTranslationHidden.value = isTranslationHiddenForMessage(
    conversationId.value,
    id.value
  );

  try {
    await ensureLoaded({ accountId: route.params.accountId });
  } catch (_error) {
    // Ignore preference-loading errors and keep default rendering behavior.
  }
});
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <template v-if="shouldRenderInlineTranslation && content">
        <FormattedContent :content="content" />
        <div
          v-if="!isTranslationHidden && translationContent"
          class="flex flex-col gap-3"
        >
          <div class="w-full h-px bg-n-slate-8" />
          <FormattedContent :content="translationContent" />
        </div>
        <div class="translation-menu relative flex justify-start">
          <button
            type="button"
            class="skip-context-menu inline-flex items-center justify-center rounded px-0.5 py-0 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': showTranslationMenu }"
            @click="showTranslationMenu = !showTranslationMenu"
          >
            <span class="i-lucide-ellipsis size-3" />
          </button>
          <DropdownMenu
            v-if="showTranslationMenu"
            v-on-clickaway="() => (showTranslationMenu = false)"
            :menu-items="translationMenuItems"
            class="z-[100] w-48 mt-2 overflow-y-auto ltr:left-0 rtl:right-0 top-full"
            @action="handleTranslationMenuAction"
          />
        </div>
      </template>
      <template v-else>
        <FormattedContent v-if="renderContent" :content="renderContent" />
        <TranslationToggle
          v-if="showTranslationToggle"
          class="-mt-3"
          :showing-original="showOriginalTranslationContent"
          @toggle="toggleTranslatedContentView"
        />
      </template>
      <div
        v-if="hasAutoTranslate"
        class="rounded-md bg-n-alpha-3 px-2 py-1.5 text-xs text-n-slate-12"
      >
        <button
          type="button"
          class="w-full text-left text-n-slate-11"
          @click="toggleAutoTranslate"
        >
          {{
            showAutoTranslate
              ? $t('CONVERSATION.TRANSLATION.HIDE_ORIGINAL')
              : $t('CONVERSATION.TRANSLATION.SHOW_ORIGINAL')
          }}
        </button>
        <FormattedContent
          v-if="autoTranslateOriginal && showAutoTranslate"
          class="mt-1 text-sm"
          :content="autoTranslateOriginal"
        />
      </div>
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}

.translation-menu .rounded-xl button {
  height: 22px;
  padding: 3px 6px;
  font-size: 10px;
  gap: 4px;
}
.translation-menu .rounded-xl button .size-3\.5 {
  width: 12px;
  height: 12px;
}
.translation-menu .rounded-xl {
  padding: 6px 8px;
}
</style>
